import { Controller } from "@hotwired/stimulus"

// Persistent admin typeahead for jumping from any admin screen directly to a
// roofer's existing Sales Calls show page. Result copy is rendered with DOM
// textContent (never innerHTML) because business names are database content.
export default class extends Controller {
  static targets = ["input", "results", "status"]
  static values = { url: String }

  connect() {
    this.activeIndex = -1
    this.items = []
    this.requestSequence = 0
    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    this.onShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        this.inputTarget.focus()
        this.inputTarget.select()
      }
    }
    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onShortcut)
  }

  disconnect() {
    clearTimeout(this.timeout)
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onShortcut)
  }

  search() {
    clearTimeout(this.timeout)
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.requestSequence += 1
      this.close()
      return
    }

    this.timeout = setTimeout(() => this.fetchResults(query), 200)
  }

  showResults() {
    if (this.items.length > 0) this.open()
  }

  navigate(event) {
    if (event.key === "Escape") {
      this.close()
      return
    }

    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key) || this.items.length === 0) return

    event.preventDefault()
    if (event.key === "ArrowDown") this.setActive(this.activeIndex + 1)
    if (event.key === "ArrowUp") this.setActive(this.activeIndex - 1)
    if (event.key === "Enter" && this.activeIndex >= 0) {
      window.location.assign(this.items[this.activeIndex].call_url)
    }
  }

  async fetchResults(query) {
    const sequence = ++this.requestSequence
    this.renderMessage("Searching roofers...")

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error(`Search failed (${response.status})`)

      const items = await response.json()
      if (sequence !== this.requestSequence || this.inputTarget.value.trim() !== query) return

      this.items = items
      this.activeIndex = -1
      if (items.length === 0) {
        this.renderMessage(`No roofers found for “${query}”.`)
      } else {
        this.renderResults()
      }
    } catch (_error) {
      if (sequence !== this.requestSequence) return
      this.items = []
      this.renderMessage("Search is unavailable. Please try again.")
    }
  }

  renderResults() {
    this.resultsTarget.replaceChildren()
    this.items.forEach((business, index) => {
      this.resultsTarget.append(this.resultElement(business, index))
    })
    this.statusTarget.textContent = `${this.items.length} roofer${this.items.length === 1 ? "" : "s"} found.`
    this.open()
  }

  resultElement(business, index) {
    const link = document.createElement("a")
    link.href = business.call_url
    link.id = `sales-call-search-result-${index}`
    link.className = "flex items-center gap-3 rounded-lg px-3 py-3 text-left transition hover:bg-[#F5F3EF] focus:bg-[#F5F3EF] focus:outline-none"
    link.setAttribute("role", "option")
    link.setAttribute("aria-selected", "false")

    const avatar = document.createElement("span")
    avatar.className = "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#E7EFE9] text-xs font-bold text-[#315F45]"
    avatar.textContent = this.initials(business.name)

    const details = document.createElement("span")
    details.className = "min-w-0 flex-1"

    const nameRow = document.createElement("span")
    nameRow.className = "flex items-center gap-2"

    const name = document.createElement("span")
    name.className = "truncate text-sm font-semibold text-[#2D2927] font-['Inter']"
    name.textContent = business.name || "Unnamed roofer"
    nameRow.append(name)

    if (business.claimed) {
      const claimed = document.createElement("span")
      claimed.className = "shrink-0 rounded-full bg-[#EFEDEA] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#6C6661]"
      claimed.textContent = "Claimed"
      nameRow.append(claimed)
    }

    const meta = document.createElement("span")
    meta.className = "mt-1 block truncate text-xs text-[#7B746E] font-['Inter']"
    meta.textContent = this.metadata(business)
    details.append(nameRow, meta)

    const action = document.createElement("span")
    action.className = "hidden shrink-0 text-xs font-semibold text-[#3E7C5A] sm:block font-['Inter']"
    action.textContent = "Open call screen →"

    link.append(avatar, details, action)
    return link
  }

  metadata(business) {
    const location = [business.city, business.state].filter(Boolean).join(", ")
    const rating = business.rating ? `★ ${business.rating}` : null
    const reviews = business.review_count ? `${business.review_count} reviews` : null
    const phone = business.phone || "No phone"
    return [location, rating, reviews, phone].filter(Boolean).join(" · ")
  }

  initials(name) {
    return (name || "R")
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
  }

  renderMessage(message) {
    const row = document.createElement("p")
    row.className = "px-3 py-5 text-center text-sm text-[#7B746E] font-['Inter']"
    row.textContent = message
    this.resultsTarget.replaceChildren(row)
    this.statusTarget.textContent = message
    this.open()
  }

  setActive(index) {
    const options = Array.from(this.resultsTarget.querySelectorAll("[role='option']"))
    if (options.length === 0) return

    this.activeIndex = (index + options.length) % options.length
    options.forEach((option, optionIndex) => {
      const active = optionIndex === this.activeIndex
      option.setAttribute("aria-selected", active.toString())
      option.classList.toggle("bg-[#F5F3EF]", active)
    })
    this.inputTarget.setAttribute("aria-activedescendant", options[this.activeIndex].id)
    options[this.activeIndex].scrollIntoView({ block: "nearest" })
  }

  open() {
    this.resultsTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.resultsTarget.classList.add("hidden")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.activeIndex = -1
  }
}
